import type {
  AgentAssetAttachment,
  AgentPlanContract,
  AgentPlanExecutionContract,
} from '@nest-react/domain';

import type { AgentChatUiMessage } from '~/features/studio/AgentChatThread';
import type {
  AgentGalleryFocus,
  AgentGalleryItem,
} from '~/features/studio/AgentChatGallery';
import type { AgentReferenceImage } from '~/features/studio/AgentReferenceImageList';
import type { DirectVideoSetupContext } from '~/features/studio/composer/types';

/** i18n key in the `studio` namespace for an unset session title. */
export const DEFAULT_AGENT_COMPOSER_SESSION_TITLE_I18N_KEY =
  'agentUi.newChat' as const;

/** Stored value when the session has no custom title yet. Display via i18n. */
export const DEFAULT_AGENT_COMPOSER_SESSION_TITLE = '';

export type AgentComposerSessionSnapshot = {
  input: string;
  referenceImages: AgentReferenceImage[];
  assetAttachments: AgentAssetAttachment[];
  assetAttachmentError: string | null;
  messages: AgentChatUiMessage[];
  activeSkillId: string | null;
  directVideoSetup: DirectVideoSetupContext | null;
  loading: boolean;
  isStreaming: boolean;
  isThinking: boolean;
  activePlan: AgentPlanContract | null;
  planning: boolean;
  approvingPlanStepId: string | null;
  confirmingPlanStepId: string | null;
  recoveringPlanStepId: string | null;
  activePlanExecutions: Record<string, AgentPlanExecutionContract>;
  sessionEngaged: boolean;
  sessionLoading: boolean;
  persistedSessionId: string | undefined;
  localDraftSessionId: string | null;
  suppressRouteSessionId: boolean;
  sessionTitle: string;
  titleLocked: boolean;
  pendingFirstInput: boolean;
  isHydrating: boolean;
  hydrationGeneration: number;
  skipNextGalleryRefresh: boolean;
  remoteGalleryItems: AgentGalleryItem[];
  galleryLoading: boolean;
  galleryStageId: string | null;
  galleryTab: 'generations' | 'cuts';
  editorProjectId: string;
  editorOpening: boolean;
  galleryFocus: AgentGalleryFocus | null;
  pendingLaunchToken: string | null;
};

export type AgentComposerSessionInit = {
  sessionLoading?: boolean;
  patch?: Partial<AgentComposerSessionSnapshot>;
};

export const buildAgentComposerSessionKey = (
  workspaceId: string,
  routeSessionId?: string
): string => `${workspaceId}:${routeSessionId ?? 'draft'}`;

export const emptyAgentComposerSessionSnapshot = (
  init: AgentComposerSessionInit = {}
): AgentComposerSessionSnapshot => ({
  input: '',
  referenceImages: [],
  assetAttachments: [],
  assetAttachmentError: null,
  messages: [],
  activeSkillId: null,
  directVideoSetup: null,
  loading: false,
  isStreaming: false,
  isThinking: false,
  activePlan: null,
  planning: false,
  approvingPlanStepId: null,
  confirmingPlanStepId: null,
  recoveringPlanStepId: null,
  activePlanExecutions: {},
  sessionEngaged: false,
  sessionLoading: init.sessionLoading ?? false,
  persistedSessionId: undefined,
  localDraftSessionId: null,
  suppressRouteSessionId: false,
  sessionTitle: DEFAULT_AGENT_COMPOSER_SESSION_TITLE,
  titleLocked: false,
  pendingFirstInput: true,
  isHydrating: false,
  hydrationGeneration: 0,
  skipNextGalleryRefresh: false,
  remoteGalleryItems: [],
  galleryLoading: false,
  galleryStageId: null,
  galleryTab: 'generations',
  editorProjectId: '',
  editorOpening: false,
  galleryFocus: null,
  pendingLaunchToken: null,
});
